/**
 * Preflight: the screen that must have rendered before any payment is
 * possible.
 *
 * It asks the server two things — the preflight (balance, totals, limits,
 * counts) and the batch (every bill with its risk summary) — and shows them
 * side by side. Every number is the server's. The shortfall is the server's.
 * The "required with margin" figure is the server's. The counts of bills by
 * band and by problem are the server's; the counts of payout accounts by
 * verification state are counts of rows on this screen, which is counting
 * and not money.
 *
 * The gate. The bill screen only unlocks its payment controls while this
 * screen's snapshot for the period is in the cache, younger than the window,
 * and describing the batch as it still reads — `gate.ts` decides, and it is
 * a pure function with the boundary under test. It is a session-local gate,
 * not a server one — the server decides whether a payment may exist — but it
 * is the gate the brief asks for: nobody reaches "pay" without having had
 * the balance, the holds and the anomaly list in front of them, recently.
 *
 * In API mode the batch is sent from here as ONE server-side run (Agent B's
 * `runBatch`: preflight at entry, sequential, paced, stop on first refusal).
 * The console asks once and renders the report; it never iterates `/pay`.
 * The count and the total in the confirmation are the preflight's, and the
 * total is retyped rather than clicked.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button.tsx';
import { Problem } from '../components/ui/primitives.tsx';
import { ApiError, payout, type BatchRun, type PayoutBill, type RiskBand } from '../lib/api.ts';
import { RiskBlock } from '../risk/pieces.tsx';
import { count, vnd, when } from './format.ts';
import { batchFingerprint, gateReasonKey, preflightGate, PREFLIGHT_WINDOW_MS, type GateState, type PreflightSnapshot } from './gate.ts';
import { keys } from './period.ts';
import { BandPill, FeatureBlock, Field, Fig, LoadFailed, Reason, Section, SettleShell, TableSkeleton } from './pieces.tsx';
import { constraintKey, isNotOnServer, refusalKey } from './refusals.ts';
import { canReadFinance, readOnlyReason, useFinanceRole } from './role.ts';

const BANDS: RiskBand[] = ['clear', 'notice', 'review', 'hold'];

/**
 * The preflight query, shared with the bill screen's gate. The snapshot
 * carries the fingerprint of the batch as it read at the same moment, so the
 * gate can tell a batch that changed underneath it.
 */
export function usePreflight(period: string, enabled = true) {
  const { role } = useFinanceRole();
  return useQuery({
    queryKey: keys.preflight(period),
    queryFn: async (): Promise<PreflightSnapshot | null> => {
      const [pre, batch] = await Promise.all([payout.preflight(period), payout.batch(period)]);
      if (pre === null) return null;
      return { ...pre, fingerprint: batchFingerprint(batch?.bills ?? []) };
    },
    enabled: enabled && canReadFinance(role),
    /**
     * Valid for the window, and gone from the cache at the window: a snapshot
     * older than five minutes is not authorisation material and is not kept
     * around to look like some.
     */
    staleTime: PREFLIGHT_WINDOW_MS,
    gcTime: PREFLIGHT_WINDOW_MS,
  });
}

/** A clock that ticks, so a gate closes while the operator is looking at it. */
export function useNow(everyMs = 10_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

/** The gate for a period, from the cached snapshot and the batch as it reads now. */
export function useGate(period: string, bills: readonly PayoutBill[] | undefined): { gate: GateState; snapshot: PreflightSnapshot | null | undefined; fetchedAt: number } {
  const pre = usePreflight(period, false);
  const now = useNow();
  const gate = preflightGate({
    snapshot: pre.data,
    fetchedAt: pre.dataUpdatedAt,
    batchFingerprint: batchFingerprint(bills ?? []),
    now,
  });
  return { gate, snapshot: pre.data, fetchedAt: pre.dataUpdatedAt };
}

export function PreflightScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { period } = useSearch({ strict: false }) as { period: string };
  const pre = usePreflight(period);
  const { role } = useFinanceRole();
  const batch = useQuery({ queryKey: keys.batch(period), queryFn: () => payout.batch(period), enabled: canReadFinance(role) });
  const mode = batch.data?.mode ?? pre.data?.mode;

  if (pre.error || batch.error) {
    return (
      <SettleShell period={period} tab="preflight" mode={mode}>
        <LoadFailed error={pre.error ?? batch.error} />
      </SettleShell>
    );
  }
  if (pre.isPending || batch.isPending || pre.data === null || batch.data === null) {
    return (
      <SettleShell period={period} tab="preflight" mode={mode}>
        <TableSkeleton />
      </SettleShell>
    );
  }

  const p = pre.data;
  const bills = batch.data.bills;
  const accounts = {
    verified: bills.filter((b) => b.account?.verify_status === 'verified').length,
    mismatch: bills.filter((b) => b.account?.verify_status === 'name_mismatch').length,
    unverified: bills.filter((b) => b.account !== null && b.account.verify_status !== 'verified' && b.account.verify_status !== 'name_mismatch').length,
    missing: bills.filter((b) => b.account === null).length,
  };
  const ranked = [...bills].sort((a, b) => b.risk.score - a.risk.score).slice(0, 20);
  const flagged = ranked.filter((b) => b.risk.flags.length > 0);
  const others = (['attempt_open', 'already_paid', 'no_account', 'account_unverified', 'under_bank_minimum', 'under_one_dong', 'risk_hold', 'line_in_exception'] as const).filter(
    (k) => p.counts[k] > 0,
  );

  return (
    <SettleShell period={period} tab="preflight" mode={mode}>
      <p className="mb-4 max-w-[62ch] text-[0.9375rem] leading-relaxed text-[var(--muted-foreground)]">{t('settle.preflight.intro')}</p>

      {/*
        Every figure on this screen is derived from the period's bills, so a
        period with none reads as 0 bills, 0 payout accounts and a zero total
        — which is true of the period and says nothing about the platform.
        Name the window, and offer the period that does hold a bill.
      */}
      {bills.length === 0 ? (
        <p className="mb-4 max-w-[62ch] text-[0.9375rem] leading-relaxed" role="status">
          {t('settle.empty.window', { from: batch.data.period_start.slice(0, 10), to: batch.data.period_end.slice(0, 10) })}{' '}
          {batch.data.latest_bill_period && batch.data.latest_bill_period !== period ? (
            <Link to="/settle/preflight" search={{ period: batch.data.latest_bill_period }} className="workspace-text-link">
              {t('settle.empty.openLatest', { period: batch.data.latest_bill_period })}
            </Link>
          ) : null}
        </p>
      ) : null}

      {/*
        The screen's one ink block, and the whole reason this screen exists:
        the period's total, the sentence that says a transfer can still be
        refused, and — as its action — the server's own verdict on whether the
        batch may be sent at all. The balance, the requirement and the
        shortfall stay on paper underneath: they explain the figure, they are
        not four figures of equal weight.
      */}
      <FeatureBlock
        label={t('settle.preflight.total')}
        figure={vnd(p.total_vnd, locale)}
        sentence={t('ui.b.settle.total.sentence')}
        action={
          <div>
            {p.ok ? (
              <p className="text-[0.9375rem] font-semibold text-[var(--stage-fg)]" role="status">
                {t('settle.preflight.ok', { payable: count(p.payable, locale), bills: count(p.bills, locale) })}
              </p>
            ) : (
              <>
                <p className="text-[0.9375rem] font-semibold text-[var(--foreground)]" role="status">
                  {t('settle.preflight.refused')}
                </p>
                {p.refusal ? (
                  <p className="mt-1 text-[0.8125rem] text-[var(--stage-mid)]">
                    {t('settle.preflight.serverSaid')}:{' '}
                    <span className="num text-[var(--stage-fg)]">{p.refusal}</span>
                  </p>
                ) : null}
              </>
            )}
            <p className="mt-2 text-[0.75rem] text-[var(--stage-mid)]">
              {t('settle.preflight.ranAt', { at: when(new Date(pre.dataUpdatedAt).toISOString(), locale) })} ·{' '}
              <button
                type="button"
                className="font-semibold text-[var(--foreground)] underline underline-offset-2"
                onClick={() => void pre.refetch()}
              >
                {t('settle.preflight.rerun')}
              </button>
            </p>
          </div>
        }
      />

      <dl className="mt-5 grid gap-5 border-t border-[var(--border)] pt-5 sm:grid-cols-3">
        <Fig
          label={t('settle.preflight.balance')}
          value={p.balance_vnd === null ? '—' : vnd(p.balance_vnd, locale)}
          hint={p.balance_vnd === null ? t('settle.preflight.balance.none') : undefined}
        />
        <Fig label={t('settle.preflight.required')} value={vnd(p.required_vnd, locale)} hint={t('settle.preflight.required.hint')} />
        <Fig
          label={t('settle.preflight.shortfall')}
          value={vnd(p.shortfall_vnd, locale)}
          tone={p.shortfall_vnd > 0 ? 'warn' : 'default'}
        />
      </dl>

      {/* --- The counts. --- */}
      <div className="mt-6 grid gap-6 border-t border-[var(--border)] pt-5 md:grid-cols-3 md:gap-8">
        <div>
          <Section title={t('settle.preflight.bands')}>
            <ul className="space-y-2">
              {BANDS.map((band) => (
                <li key={band} className="flex items-center justify-between gap-3">
                  <BandPill band={band} size="sm" />
                  <span className="num text-[1.0625rem] font-semibold">{count(p.risk_bands[band], locale)}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
        <div>
          <Section title={t('settle.preflight.accounts')}>
            <ul className="space-y-2 text-[0.875rem]">
              {(
                [
                  ['verified', accounts.verified],
                  ['unverified', accounts.unverified],
                  ['mismatch', accounts.mismatch],
                  ['missing', accounts.missing],
                ] as const
              ).map(([k, n]) => (
                <li key={k} className="flex items-center justify-between gap-3">
                  <span>{t(`settle.preflight.accounts.${k}`)}</span>
                  <span className="num text-[1.0625rem] font-semibold">{count(n, locale)}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
        <div>
          <Section title={t('settle.preflight.limits')}>
            <ul className="space-y-2 text-[0.875rem]">
              <li className="flex items-center justify-between gap-3">
                <span>{t('settle.preflight.ceiling', { ceiling: vnd(p.bank_ceiling_vnd, locale) })}</span>
                <span className="num text-[1.0625rem] font-semibold">{count(p.counts.over_bank_ceiling, locale)}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span>{p.cap_vnd === null ? t('settle.preflight.cap.none') : t('settle.preflight.cap', { cap: vnd(p.cap_vnd, locale) })}</span>
                <span className="num text-[1.0625rem] font-semibold">{p.cap_vnd === null ? '—' : count(p.counts.over_cap, locale)}</span>
              </li>
            </ul>
            {others.length > 0 ? (
              <>
                <h3 className="mt-4 text-[0.8125rem] font-semibold text-[var(--foreground)]">{t('settle.preflight.others')}</h3>
                <ul className="mt-2 space-y-1.5 text-[0.8125rem]">
                  {others.map((k) => (
                    <li key={k} className="flex items-center justify-between gap-3">
                      <span>{t(`settle.issue.${k}`)}</span>
                      <span className="num font-semibold">{count(p.counts[k], locale)}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </Section>
        </div>
      </div>

      {/* --- The anomaly list. --- */}
      <div className="mt-8 border-t border-[var(--border)] pt-5">
        <Section title={t('settle.preflight.anomalies')}>
          <p className="text-[0.8125rem] leading-snug text-[var(--muted-foreground)]">
            {t('settle.preflight.anomalies.hint', { n: count(ranked.length, locale) })}
          </p>
          {flagged.length === 0 ? (
            <p className="mt-3 text-[0.875rem]">{t('settle.preflight.anomalies.none')}</p>
          ) : (
            <ol className="mt-3 divide-y divide-[var(--border)]">
              {flagged.map((b) => (
                <li key={b.id} className="py-3">
                  <p className="num mb-1 text-[0.875rem] font-semibold">
                    <Link to="/settle/bills/$billId" params={{ billId: b.id }} search={{ period }} className="text-[var(--tech-ink)]">
                      {b.collector_ref}
                    </Link>
                  </p>
                  <RiskBlock summary={b.risk} period={period} billId={b.id} />
                </li>
              ))}
            </ol>
          )}
        </Section>
      </div>

      {/* --- What happens next. --- */}
      <div className="mt-8 border-t border-[var(--border)] pt-5">
        {mode === 'api' ? (
          <ApiBatch snapshot={p} fetchedAt={pre.dataUpdatedAt} bills={bills} period={period} />
        ) : (
          <div>
            <p className="max-w-[62ch] text-[0.9375rem] leading-relaxed">{t('settle.preflight.continue.manual')}</p>
            <Button asChild variant="primary" className="mt-4">
              <Link to="/settle" search={{ period }}>
                {t('settle.tab.bills')}
              </Link>
            </Button>
          </div>
        )}
      </div>
    </SettleShell>
  );
}

/* -------------------------------------------------------------------------
   The API rail: "send N transfers totalling X", as one server-side run.
   The console asks once and renders the report. If the server has no run
   route, the answer is 404 and the screen says exactly that.
   ---------------------------------------------------------------------- */

function ApiBatch({ snapshot: p, fetchedAt, bills, period }: { snapshot: PreflightSnapshot; fetchedAt: number; bills: PayoutBill[]; period: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const client = useQueryClient();
  const { role } = useFinanceRole();
  const readOnly = readOnlyReason(role);
  const now = useNow();
  const gate = preflightGate({ snapshot: p, fetchedAt, batchFingerprint: batchFingerprint(bills), now });
  const [typed, setTyped] = useState('');
  const [notOnServer, setNotOnServer] = useState(false);
  const [refused, setRefused] = useState<unknown>(null);

  const run = useMutation({
    mutationFn: () => payout.runBatch(period),
    onSuccess: () => {
      setRefused(null);
      void client.invalidateQueries({ queryKey: keys.batch(period) });
      void client.invalidateQueries({ queryKey: keys.preflight(period) });
    },
    onError: (err) => {
      if (isNotOnServer(err)) setNotOnServer(true);
      else setRefused(err);
    },
  });

  const collectorOf = (billId: string) => bills.find((b) => b.id === billId)?.collector_ref ?? billId;
  const matches = typed !== '' && typed === String(p.total_vnd);
  const gateKey = gateReasonKey(gate);
  const blocked =
    readOnly !== null
      ? t(readOnly)
      : gateKey !== null
        ? t(gateKey)
        : !p.ok || p.payable === 0
          ? t('settle.batch.noneOk')
          : notOnServer
            ? t('settle.batch.notOnServer')
            : null;
  const report: (BatchRun & { aborted: boolean }) | null | undefined = run.data;

  return (
    <div>
      <Section title={t('settle.batch.title')}>
        <p className="text-[1.0625rem] font-semibold">
          {t('settle.batch.sentence', { n: count(p.payable, locale), total: vnd(p.total_vnd, locale) })}
        </p>
        <p className="mt-1 text-[0.8125rem] text-[var(--muted-foreground)]">{t('settle.batch.serverLoop')}</p>
        {blocked ? (
          <>
            <Button variant="primary" className="mt-4" disabled aria-describedby="batch-reason">
              {t('settle.batch.send', { n: count(p.payable, locale) })}
            </Button>
            <Reason id="batch-reason">{blocked}</Reason>
          </>
        ) : (
          <form
            className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,20rem)_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (matches && !run.isPending) run.mutate();
            }}
          >
            <Field
              label={t('settle.batch.retype')}
              name="total"
              inputMode="numeric"
              pattern="\d+"
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value.replace(/\D/g, ''))}
              hint={typed !== '' && !matches ? t('settle.batch.mismatch') : `${p.total_vnd}`}
              aria-invalid={typed !== '' && !matches}
              required
            />
            <Button type="submit" variant="primary" disabled={!matches || run.isPending} aria-describedby="batch-reason">
              {run.isPending ? t('bo.working') : t('settle.batch.send', { n: count(p.payable, locale) })}
            </Button>
            <Reason id="batch-reason">{t('settle.batch.retype.hint')}</Reason>
          </form>
        )}

        {refused !== null ? (
          <div className="mt-4">
            <Problem title={t('bo.refused')} body={t(refusalKey(refused))} reference={refused instanceof ApiError ? refused.ref : undefined} />
          </div>
        ) : null}

        {report ? <RunReport report={report} collectorOf={collectorOf} period={period} /> : null}
      </Section>
    </div>
  );
}

/**
 * The server's report of a run, as sentences.
 *
 * Four facts, and they are different facts. `sent` is what left. `refused` is
 * every bill the run would not send and why — it is populated from the
 * preflight's issue list before the loop starts, so it is usually the longest
 * part of the report and the part somebody has to work through. `stopped_at`
 * is the single bill the loop halted on. `tickets` is what the run wrote for a
 * person to pick up.
 *
 * The aborted case is separated deliberately. When `payBill` throws, the
 * worker leaves `stopped_at` set but never pushes that bill onto `refused`,
 * and the route drops the constraint on its way out (`stopped_at` serialises
 * as a bare bill id). The old code met that with
 * `refused.find(...) ?? 'payout_transfer_rejected'` and told the operator the
 * transfer had been rejected — a cause that never happened, on the screen
 * where money has just moved. An aborted run now says it stopped on an error
 * and names no reason it does not have.
 */
function RunReport({
  report,
  collectorOf,
  period,
}: {
  report: BatchRun & { aborted: boolean };
  collectorOf: (billId: string) => string;
  period: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  /** A ticket kind is a machine constant; print the sentence when there is one, the constant when there is not. */
  const ticketLabel = (kind: string) => {
    const key = `settle.ticket.${kind}`;
    return t(key) === key ? kind : t(key);
  };
  const stoppedReason = report.refused.find((r) => r.bill_id === report.stopped_at)?.constraint;
  return (
    <div className="mt-4" aria-live="polite">
      {report.aborted ? (
        <div className="mb-3">
          <Problem
            title={t('settle.batch.aborted')}
            body={
              report.stopped_at === null
                ? t('settle.batch.aborted.body')
                : `${t('settle.batch.aborted.at', { collector: collectorOf(report.stopped_at) })} ${t('settle.batch.aborted.body')}`
            }
          />
        </div>
      ) : null}

      {!report.preflight.ok ? (
        <Problem
          title={t('settle.preflight.refused')}
          body={t('settle.batch.refusedAtSend', { reason: report.preflight.refusal ?? '—' })}
        />
      ) : null}

      {report.sent.length > 0 ? (
        <ol className="divide-y divide-[var(--border)] text-[0.875rem]">
          {report.sent.map((s) => (
            <li key={s.bill_id} className="flex items-center justify-between gap-3 py-1.5">
              <span className="num font-semibold">{collectorOf(s.bill_id)}</span>
              <span className="num text-[var(--muted-foreground)]">{t(`settle.attempt.${s.status}`)}</span>
            </li>
          ))}
        </ol>
      ) : null}

      {!report.aborted && report.stopped_at !== null && stoppedReason !== undefined ? (
        <div className="mt-3">
          <Problem
            title={t('bo.refused')}
            body={t('settle.batch.stopped', {
              collector: collectorOf(report.stopped_at),
              reason: t(constraintKey(stoppedReason)),
            })}
          />
        </div>
      ) : !report.aborted && report.stopped_at === null && report.preflight.ok ? (
        <p className="mt-3 text-[0.9375rem] font-semibold" role="status">
          {t('settle.batch.done', { n: count(report.sent.length, locale) })}
        </p>
      ) : null}

      {report.refused.length > 0 ? (
        <Section className="mt-5" title={t('settle.batch.refused.title')}>
          <p className="mb-2 text-[0.8125rem] leading-relaxed text-[var(--muted-foreground)]">
            {t('settle.batch.refused.body')}
          </p>
          <ul className="divide-y divide-[var(--border)] text-[0.875rem]">
            {report.refused.map((r) => (
              <li key={r.bill_id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-1.5">
                <Link
                  to="/settle/bills/$billId"
                  params={{ billId: r.bill_id }}
                  search={{ period }}
                  className="num font-semibold text-[var(--tech-ink)]"
                >
                  {r.collector_ref}
                </Link>
                <span className="text-[var(--muted-foreground)]">{t(constraintKey(r.constraint))}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {report.tickets.length > 0 ? (
        <Section className="mt-5" title={t('settle.batch.tickets.title')}>
          <p className="mb-2 text-[0.8125rem] leading-relaxed text-[var(--muted-foreground)]">
            {t('settle.batch.tickets.body')}
          </p>
          <ul className="divide-y divide-[var(--border)] text-[0.875rem]">
            {report.tickets.map((k, i) => (
              <li
                key={`${k.kind}-${k.occurred_at}-${i}`}
                className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-1.5"
              >
                <span className="font-semibold">{ticketLabel(k.kind)}</span>
                <span className="num text-[var(--muted-foreground)]">
                  {k.bill_id === null ? null : (
                    <Link
                      to="/settle/bills/$billId"
                      params={{ billId: k.bill_id }}
                      search={{ period }}
                      className="mr-3 text-[var(--tech-ink)]"
                    >
                      {collectorOf(k.bill_id)}
                    </Link>
                  )}
                  {when(k.occurred_at, locale)}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}
